import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";
import CancelIcon from "../../Atomos/Icons/Close";
import SaveChanges from "../../Atomos/Icons/EditMode/SaveChanges";
import {
  memberModalPrimaryButtonClassName,
  memberModalSecondaryButtonClassName,
} from "./memberModalFooterButtonClasses";
import InputTextArea from "../../staff/InputTextArea";
import DropDownMultiple, {
  type DropDownOptionBase,
} from "../../staff/DropDownMultiple";
import Button from "../../staff/Button";
import { IPerson } from "../../../interfaces/models/IPerson";
import type { ILanguage } from "../../../interfaces/models/ILanguage";
import type { IPersonView } from "../../../interfaces/models/IPersonView";
import type { IPersonCreate } from "../../../interfaces/models/IPersonCreate";
import {
  ipersonToPersonCreate,
  mapPersonCreateToSavePayload,
} from "../../Organismos/Person/personForm/mapPerson";
import { updatePerson } from "../../../Services/Person";
import AddLanguageForm from "../../../pages/Catalogs/CRUD/AddLanguageForm";
import { toast } from "react-toastify";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

type EditGeneralProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  person: IPerson;
  /** Language catalog (same source as profile overview / Manage Firm form). */
  languageCatalog: ILanguage[];
  /** When a new language is created via Add Language, merge into parent catalog so the dropdown stays in sync. */
  onLanguageAddedToCatalog?: (language: ILanguage) => void;
  onSaved: () => void;
};

function normalizeStringArray(raw: unknown): string[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Align with Manage Firm → Professionals: `languages` on the person are catalog language names (see `Languages.tsx`). */
function personLanguagesToNames(
  person: IPerson,
  catalog: ILanguage[],
): string[] {
  const raw = normalizeStringArray(person.languages as unknown);
  const byId = new Map(catalog.map((l) => [l.id, l.name]));
  const byNameLower = new Map(
    catalog.map((l) => [l.name.trim().toLowerCase(), l.name]),
  );
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    let name = byId.get(r);
    if (!name) {
      name = byNameLower.get(r.toLowerCase());
    }
    if (!name) {
      name = r;
    }
    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(name);
    }
  }
  return out;
}

function barAdmissionsToString(person: IPerson): string {
  const raw = person.barAdmissions as unknown;
  if (raw == null) {
    return "";
  }
  if (typeof raw === "string") {
    return raw.trim();
  }
  if (Array.isArray(raw)) {
    return raw
      .map((x) => String(x).trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(raw).trim();
}

function educationToString(person: IPerson): string {
  return person.education?.trim() ?? "";
}

const EditGeneralProfileModal: React.FC<EditGeneralProfileModalProps> = ({
  isOpen,
  onClose,
  person,
  languageCatalog,
  onLanguageAddedToCatalog,
  onSaved,
}) => {
  const catalog = languageCatalog;
  const [bio, setBio] = useState("");
  const [languageNames, setLanguageNames] = useState<string[]>([]);
  const [barAdmissions, setBarAdmissions] = useState("");
  const [education, setEducation] = useState("");
  const [addLanguageOpen, setAddLanguageOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Avoid resetting draft when `catalog` finishes loading after open. */
  const draftInitKeyRef = useRef<string | null>(null);
  const { isStaffMode } = useStaffMode();

  useEffect(() => {
    if (!isOpen) {
      draftInitKeyRef.current = null;
      return;
    }
    const key = `${person.id ?? ""}|${person._ts ?? ""}`;
    if (draftInitKeyRef.current === key) {
      return;
    }
    draftInitKeyRef.current = key;
    setBio(person.bio?.trim() ?? "");
    setBarAdmissions(barAdmissionsToString(person));
    setEducation(educationToString(person));
    setLanguageNames(personLanguagesToNames(person, catalog));
  }, [isOpen, person, catalog]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const dropdownOptions = useMemo(() => {
    const extra: ILanguage[] = [];
    for (const name of languageNames) {
      if (!catalog.some((l) => l.name === name)) {
        extra.push({
          id: `__extra__:${name}`,
          name,
          abbreviation: "",
        });
      }
    }
    return [...catalog, ...extra].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }, [catalog, languageNames]);

  const dropdownValue = useMemo((): DropDownOptionBase[] => {
    return languageNames.map((name) => {
      const found = catalog.find((l) => l.name === name);
      return {
        id: String(found?.id ?? `__extra__:${name}`),
        name,
      };
    });
  }, [languageNames, catalog]);

  const handleLanguageDropdownChange = useCallback(
    (value: DropDownOptionBase[] | null) => {
      if (!value?.length) {
        setLanguageNames([]);
        return;
      }
      setLanguageNames(value.map((v) => v.name));
    },
    [],
  );

  const removeLanguage = useCallback((name: string) => {
    setLanguageNames((prev) => prev.filter((n) => n !== name));
  }, []);

  const handleNewLanguageCreated = useCallback(
    (lang: ILanguage) => {
      onLanguageAddedToCatalog?.(lang);
      setLanguageNames((prev) => {
        if (prev.includes(lang.name)) {
          return prev;
        }
        return [...prev, lang.name];
      });
      setAddLanguageOpen(false);
    },
    [onLanguageAddedToCatalog],
  );

  const handleSave = async () => {
    if (!person.id?.trim()) {
      toast.error("Missing profile id.");
      return;
    }
    setSaving(true);
    try {
      /** Same shape as Manage Firm / person form: `username`, AOP ids, etc. Raw `IPerson` breaks `/person` validation. */
      const form: IPersonCreate = {
        ...ipersonToPersonCreate(person),
        bio,
        languages: languageNames,
        barAdmissions,
        education,
      };
      const payload = mapPersonCreateToSavePayload(
        form,
        person as unknown as IPersonView,
      );
      await updatePerson(payload);
      toast.success("Profile updated.");
      onSaved();
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        typeof data === "string" && data.trim()
          ? data.trim()
          : err instanceof Error
            ? err.message
            : "Could not save profile.";
      toast.error(message);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const cardClassName =
    "flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl";

  const pad = "px-6 md:px-8";

  const card = (
    <div
      className={cardClassName}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-labelledby="edit-general-profile-title"
    >
      <div
        className={`flex shrink-0 items-start justify-between gap-4 ${pad} pt-6 md:pt-8`}
      >
        <h2
          id="edit-general-profile-title"
          className="font-decimal text-2xl font-semibold text-primary"
        >
          General Profile
        </h2>
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-gray-font hover:text-gray-600"
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
        >
          <CloseModal />
        </button>
      </div>

      <div
        className={`min-h-0 flex-1 overflow-y-auto ${pad} pb-4 pt-6 md:pt-6`}
      >
        <div className="flex flex-col gap-8">
          <section>
            <h3 className="mb-3 font-decimal text-lg font-semibold text-primary">
              Professional Bio
            </h3>
            <InputTextArea
              title="Summary"
              value={bio}
              onChange={setBio}
              placeholder="ex: I am a lawyer with 10 years of experience in the field of law."
              maxChars={4000}
              showCharacterCount
              minHeightClassName="min-h-[200px]"
            />
          </section>

          <section className="flex min-w-0 flex-col gap-3">
            <h3 className="font-decimal text-lg font-semibold text-primary">
              Languages
            </h3>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <DropDownMultiple<DropDownOptionBase>
                  title=""
                  placeholder="Search languages"
                  options={dropdownOptions.map((l) => ({
                    id: String(l.id ?? l.name),
                    name: l.name,
                  }))}
                  value={dropdownValue}
                  onChange={handleLanguageDropdownChange}
                  multiple
                  searchable
                  sortByName
                />
              </div>
              <Button
                text="Add Language(s) +"
                onClick={() => setAddLanguageOpen(true)}
                color={isStaffMode ? "secondary" : "primary"}
                variant="filled"
                size="fixed"
                disabled={saving}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {languageNames.map((name) => (
                <div
                  key={name}
                  className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-font"
                >
                  <span>{name}</span>
                  <button
                    type="button"
                    className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-font hover:text-primary"
                    onClick={() => removeLanguage(name)}
                    disabled={saving}
                    aria-label={`Remove ${name}`}
                  >
                    <span className="flex h-3.5 w-3.5">
                      <CloseModal />
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-1 font-decimal text-lg font-semibold text-primary">
              Bar Admissions
            </h3>
            <p className="mb-3 text-xs text-gray-500">
              Enriched text: you can paste basic HTML (e.g. paragraphs, lists,
              links). It will render on your profile when saved.
            </p>
            <InputTextArea
              title=""
              value={barAdmissions}
              onChange={setBarAdmissions}
              placeholder="e.g. New York State Bar, or formatted notes with HTML"
              maxChars={1000}
              showCharacterCount
              minHeightClassName="min-h-[200px]"
            />
          </section>

          <section>
            <h3 className="mb-1 font-decimal text-lg font-semibold text-primary">
              Education
            </h3>
            <p className="mb-3 text-xs text-gray-500">
              Enriched text: you can paste basic HTML (e.g. schools, degrees,
              years). It will render on your profile when saved.
            </p>
            <InputTextArea
              title=""
              value={education}
              onChange={setEducation}
              placeholder="e.g. University, degree, years, and notes — HTML optional"
              maxChars={1000}
              showCharacterCount
              minHeightClassName="min-h-[200px]"
            />
          </section>
        </div>
      </div>

      <div
        className={`flex flex-wrap justify-end gap-3 border-t border-gray-200 ${pad} py-5`}
      >
        <button
          type="button"
          className={memberModalSecondaryButtonClassName}
          onClick={onClose}
          disabled={saving}
        >
          <span>Cancel</span>
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-font">
            <CancelIcon className="h-4 w-4" />
          </span>
        </button>
        <button
          type="button"
          className={memberModalPrimaryButtonClassName}
          onClick={() => void handleSave()}
          disabled={saving}
        >
          <span>Update Information</span>
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-white [&_svg]:fill-current">
            <SaveChanges />
          </span>
        </button>
      </div>
    </div>
  );

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
        onClick={saving ? undefined : onClose}
        role="presentation"
      >
        {card}
      </div>

      {addLanguageOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4"
              onClick={() => setAddLanguageOpen(false)}
              role="presentation"
            >
              <div
                className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="new-language-title"
              >
                <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-5 pt-5">
                  <h2
                    id="new-language-title"
                    className="text-lg font-semibold text-gray-font"
                  >
                    New language
                  </h2>
                  <button
                    type="button"
                    className="flex h-8 w-8 shrink-0 items-center justify-center text-gray-font hover:text-gray-600"
                    onClick={() => setAddLanguageOpen(false)}
                    aria-label="Close"
                  >
                    <CloseModal />
                  </button>
                </div>
                <div className="overflow-y-auto px-5 pb-5 pt-4">
                  <AddLanguageForm
                    isActive={addLanguageOpen}
                    onLanguageCreated={handleNewLanguageCreated}
                    onCancel={() => setAddLanguageOpen(false)}
                  />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>,
    document.body,
  );
};

export default EditGeneralProfileModal;
